import { ChevronLeftIcon } from '@heroicons/react/16/solid'
import { PlusIcon } from '@heroicons/react/20/solid'
import { useState } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { AreaTreemap } from '../catalyst/area-treemap'
import { Button } from '../catalyst/button'
import { Heading, Subheading } from '../catalyst/heading'
import { Link } from '../catalyst/link'
import { Text } from '../catalyst/text'
import { formatArea, orgAreaUnit } from '../lib/area'
import { useAuth } from '../lib/auth'
import { farmBadgeProps, useCanadaGapFarmStatus } from '../lib/canadagap'
import { ComplianceBadge } from '../lib/compliance-badge'
import { useFarms } from '../lib/farms'
import { useFields, type NewFieldBody } from '../lib/fields'
import type { CanadaGapFarmFormKey } from '../lib/types'
import { FarmFormDialog } from './farm-form-dialog'
import { FieldFormDialog } from './field-form-dialog'
import { LogButton } from '../wizard/log-button'
import { Shell } from './shell'

/** The farm-level CanadaGAP records: kept per farm (buildings, equipment, people), not per field. */
const FARM_RECORDS: { form: CanadaGapFarmFormKey; title: string; formName: string; path: string }[] = [
  { form: 'g', title: 'Building inspections', formName: 'Cleaning, Maintenance and Repair of Buildings', path: 'building-inspections' },
  { form: 'i', title: 'Equipment cleaning, maintenance and calibration', formName: 'Equipment Cleaning, Maintenance and Calibration', path: 'equipment-activities' },
  { form: 'j', title: 'Personal hygiene facilities', formName: 'Cleaning and Maintenance — Personal Hygiene Facilities', path: 'hygiene-facility-checks' },
  { form: 'k', title: 'Training sessions', formName: 'Training Session', path: 'training-sessions' },
  { form: 'l', title: 'Visitor sign-in', formName: 'Visitor Sign-In Log', path: 'visitor-entries' },
  { form: 'm', title: 'Pest monitoring', formName: 'Pest Monitoring for Buildings', path: 'pest-monitoring-checks' },
  { form: 'q', title: 'Packing and storing', formName: 'Packing, Repacking, Storing and Brokerage of Market Product', path: 'packing-records' },
  { form: 'o', title: 'Shipping', formName: 'Transporting Product', path: 'shipments' },
]

export function FarmPage() {
  const { farmId } = useParams<{ farmId: string }>()
  const { org } = useAuth()
  const areaUnit = orgAreaUnit(org)
  const { getFarm, loading: farmsLoading } = useFarms()
  const { fields, loading: fieldsLoading, error, addField } = useFields(org?.id, farmId)
  const { status: canadaGap } = useCanadaGapFarmStatus(org?.id, farmId, new Date().getFullYear())
  const [addFieldOpen, setAddFieldOpen] = useState(false)
  const [editFarmOpen, setEditFarmOpen] = useState(false)
  const canEditFarm = org?.role === 'OWNER'
  const canWriteFields = org?.role !== 'AGRONOMIST'

  const farm = farmId ? getFarm(farmId) : undefined

  if (!farmsLoading && !farm) {
    return <Navigate to="/" replace />
  }
  if (!farm || !org) return null

  return (
    <Shell>
      <Link href="/" className="inline-flex items-center gap-1 text-sm/6 font-medium text-zinc-500 dark:text-zinc-400">
        <ChevronLeftIcon className="size-4" />
        Farms
      </Link>

      <div className="mt-4 flex items-center justify-between gap-4">
        <div>
          <Heading>{farm.name}</Heading>
          <Text>{[farm.addressLine1, farm.city, farm.postalCode].filter(Boolean).join(', ') || 'No address yet'}</Text>
          {farm.latitude != null && farm.longitude != null && (
            <Text>
              {farm.latitude}, {farm.longitude} ·{' '}
              <a className="underline" href={`https://www.google.com/maps?q=${farm.latitude},${farm.longitude}`} target="_blank" rel="noreferrer">
                Open in Maps
              </a>
            </Text>
          )}
        </div>
        <div className="flex gap-3">
          {canWriteFields && <LogButton farmId={farm.id} />}
          {canEditFarm && (
            <Button outline onClick={() => setEditFarmOpen(true)}>
              Edit farm
            </Button>
          )}
          {canWriteFields && (
            <Button onClick={() => setAddFieldOpen(true)}>
              <PlusIcon />
              Add field
            </Button>
          )}
        </div>
      </div>

      <div className="mt-8">
        {error ? (
          <div className="rounded-lg border border-dashed border-red-300 p-10 text-center dark:border-red-900">
            <Text>{error}</Text>
          </div>
        ) : fieldsLoading ? (
          <div className="rounded-lg border border-dashed border-zinc-950/15 p-10 text-center dark:border-white/15">
            <Text>Loading fields…</Text>
          </div>
        ) : fields.length === 0 ? (
          <div className="rounded-lg border border-dashed border-zinc-950/15 p-10 text-center dark:border-white/15">
            <Text>No fields yet. Add the first planted field on this farm.</Text>
          </div>
        ) : (
          <>
            <AreaTreemap
              items={fields.map((field) => ({
                id: field.id,
                label: field.name,
                detail: `${formatArea(field.areaM2, areaUnit)}${field.notes ? ` · ${field.notes}` : ''}`,
                value: field.areaM2,
                href: `/farms/${farm.id}/fields/${field.id}`,
              }))}
            />
            <Text className="mt-2">Tile size is proportional to field area. Select a field to see its details.</Text>
          </>
        )}
      </div>

      <div className="mt-12 flex items-center justify-between gap-4">
        <div>
          <Subheading>Inventory</Subheading>
          <Text>The equipment and buildings this farm's records are kept against, and its stock of pesticides, fertilizers and other inputs.</Text>
        </div>
        <Link href={`/farms/${farm.id}/inventory`} className="shrink-0">
          View →
        </Link>
      </div>

      <Subheading className="mt-12">Farm records</Subheading>
      <Text>Kept for the whole farm: buildings, equipment, washrooms, training, visitors, pests, packing and shipping.</Text>
      {FARM_RECORDS.map((record, index) => (
        <div key={record.form} className={`${index === 0 ? 'mt-6' : 'mt-4'} flex items-center justify-between gap-4`}>
          <div>
            <Subheading level={3}>{record.title}</Subheading>
            <div className="flex flex-wrap gap-1.5">
              <ComplianceBadge framework="CanadaGAP" formCode={record.form.toUpperCase()} formName={record.formName} {...farmBadgeProps(canadaGap, record.form)} />
            </div>
          </div>
          <Link href={`/farms/${farm.id}/${record.path}`} className="shrink-0">
            View →
          </Link>
        </div>
      ))}

      {canEditFarm && <FarmFormDialog key={`edit-farm-${editFarmOpen}`} open={editFarmOpen} onClose={() => setEditFarmOpen(false)} farm={farm} />}
      {canWriteFields && <FieldFormDialog key={`add-field-${addFieldOpen}`} open={addFieldOpen} onClose={() => setAddFieldOpen(false)} onSave={(body) => addField(body as NewFieldBody)} />}
    </Shell>
  )
}
